import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { RefreshCw, AlertCircle, Info, AlertTriangle } from "lucide-react";

export default function Logs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ type: "", level: "" });

  useEffect(() => {
    fetchLogs();
  }, [filter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.type) params.append('type', filter.type);
      if (filter.level) params.append('level', filter.level);
      
      const response = await axios.get(`${API}/logs?${params.toString()}`);
      setLogs(response.data);
    } catch (error) {
      console.error("Erreur chargement logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const getLogIcon = (level) => {
    switch (level) {
      case 'error':
        return <AlertCircle className="text-red-500" size={18} />;
      case 'warning':
        return <AlertTriangle className="text-yellow-500" size={18} />;
      default:
        return <Info className="text-blue-500" size={18} />;
    }
  };

  const getLogBadgeClass = (level) => {
    switch (level) {
      case 'error':
        return 'badge bg-red-100 text-red-700';
      case 'warning':
        return 'badge badge-warning';
      default:
        return 'badge badge-info';
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="logs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Journal d'activité</h1>
          <p className="text-gray-600">Suivez toutes les opérations de l'outil</p>
        </div>
        <button
          onClick={fetchLogs}
          className="btn-secondary flex items-center space-x-2"
          data-testid="refresh-logs-button"
        >
          <RefreshCw size={16} />
          <span>Actualiser</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={filter.type}
              onChange={(e) => setFilter({ ...filter, type: e.target.value })}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F86EF]"
              data-testid="filter-type"
            >
              <option value="">Tous</option>
              <option value="monitoring">Veille</option>
              <option value="generation">Génération</option>
              <option value="publication">Publication</option>
              <option value="error">Erreurs</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Niveau</label>
            <select
              value={filter.level}
              onChange={(e) => setFilter({ ...filter, level: e.target.value })}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F86EF]"
              data-testid="filter-level"
            >
              <option value="">Tous</option>
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="error">Erreur</option>
            </select>
          </div>
        </div>
      </div>

      {/* Logs List */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="loading-spinner"></div>
        </div>
      ) : logs.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center" data-testid="no-logs">
          <p className="text-gray-600">Aucun log disponible</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="logs-list">
          {logs.map((log) => (
            <div
              key={log.id}
              className="bg-white rounded-lg border border-gray-100 p-4 hover:border-gray-200 transition-colors"
              data-testid={`log-${log.id}`}
            >
              <div className="flex items-start space-x-3">
                <div className="mt-0.5">{getLogIcon(log.level)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className={getLogBadgeClass(log.level)}>
                      {log.type}
                    </span>
                    <span className="text-xs text-gray-400">
                      {new Date(log.timestamp).toLocaleString('fr-FR')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-900">{log.message}</p>
                  {log.details && (
                    <details className="mt-2">
                      <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                        Voir les détails
                      </summary>
                      <pre className="mt-2 text-xs bg-gray-50 rounded p-2 overflow-x-auto">
                        {JSON.stringify(log.details, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
